import { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, Loader2, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { readJson } from "@/hooks/admin/adminApi";
import { useWaTemplates } from "@/hooks/admin/useWaTemplates";

const GUESS = ["name", "program", "url", "batch"];
const guessVars = (body, n) => {
  if (!body) return GUESS.slice(0, n).join(",");
  const used = new Set();
  const out = [];
  for (let i = 1; i <= n; i++) {
    const m = new RegExp(`([\\s\\S]{0,25})\\{\\{\\s*${i}\\s*\\}\\}([\\s\\S]{0,25})`).exec(body);
    const before = (m?.[1] || "").toLowerCase();
    const after = (m?.[2] || "").toLowerCase();
    let k = "";
    if (/batch/.test(before)) k = "batch";
    else if (/dear|hi\b|hello|namaste|your\s*$/.test(before)) k = "name";
    else if (/program/.test(after) || /program/.test(before)) k = "program";
    else if (/link|http|join|group|url/.test(before + after)) k = "url";
    if (!k || used.has(k)) k = GUESS.find((g) => !used.has(g)) || "name";
    used.add(k);
    out.push(k);
  }
  return out.join(",");
};

export const MessagePreviewModal = ({ target, adminKey, authFetch, content, onClose, onSent }) => {
  const { templates } = useWaTemplates(adminKey);
  const [tpl, setTpl] = useState("");
  const [vars, setVars] = useState("");
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const isWa = target?.channel === "whatsapp";
  const chosen = templates.find((t) => t.name === tpl);

  useEffect(() => {
    if (!target) return;
    setStep(1); setPreview(null); setSending(false);
    const def = content?.wa_welcome_template_name || "";
    setTpl(def);
    setVars(content?.wa_welcome_template_vars || "");
  }, [target, content]);

  const pick = (name) => {
    setTpl(name);
    const t = templates.find((x) => x.name === name);
    const guess = guessVars(t?.body, t?.var_count || 0);
    const known = name === content?.wa_welcome_template_name ? content?.wa_welcome_template_vars : name === content?.wa_announce_template_name ? content?.wa_announce_template_vars : "";
    setVars(known || guess);
  };

  useEffect(() => {
    if (!target || !tpl) { setPreview(null); return; }
    let alive = true;
    setLoading(true);
    const q = new URLSearchParams({ kind: target.channel, template: tpl, lang: chosen?.language || "", variables: vars });
    const t = setTimeout(async () => {
      try {
        const res = await authFetch(`/admin/registrations/${target.r.id}/message-preview?${q}`);
        const data = await readJson(res);
        if (!alive) return;
        setPreview(res.ok ? data : { error: data?.detail || "Could not load the message" });
      } catch (err) {
        if (alive) setPreview({ error: err.message });
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [target, tpl, vars, chosen?.language, authFetch]);

  if (!target) return null;
  const { r } = target;
  const to = isWa ? r.whatsapp || r.mobile : r.email;
  const mismatch = chosen && chosen.var_count !== vars.split(",").filter((x) => x.trim()).length;

  const send = async () => {
    setSending(true);
    try {
      const res = await authFetch(`/admin/registrations/${r.id}/send-template`, { method: "POST", body: JSON.stringify({ channel: target.channel, template: tpl, lang: chosen?.language || "", variables: vars }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      toast.success(`${isWa ? "WhatsApp message" : "Email"} sent successfully to ${r.name} (${tpl})`);
      onSent?.(r.id, data);
      onClose();
    } catch (err) {
      toast.error(`${isWa ? "WhatsApp message" : "Email"} NOT sent to ${r.name} — ${err.message}`, { duration: 10000 });
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-[#1A2E26]/50 backdrop-blur-sm p-4" data-testid="message-preview-modal" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-3xl bg-white shadow-[0_30px_80px_rgba(26,46,38,0.35)] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <span className={`grid place-items-center w-11 h-11 rounded-2xl ${isWa ? "bg-[#E7F8EE] text-[#128C7E]" : "bg-amber-50 text-amber-700"}`}>{isWa ? <MessageCircle size={22} /> : <Mail size={22} />}</span>
          <div>
            <h3 className="font-serif text-2xl leading-tight" data-testid="message-preview-title">{step === 1 ? "Choose a template" : isWa ? "Send on WhatsApp?" : "Send by email?"}</h3>
            <p className="text-xs text-muted-foreground" data-testid="message-preview-to">To <span className="font-medium text-foreground">{r.name}</span> · {to || (isWa ? "no WhatsApp number" : "no email on record")}</p>
          </div>
          <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="message-preview-step">Step {step} of 2</span>
        </div>

        {step === 1 ? (
          <div className="mt-5 space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Template</span>
              <select data-testid="message-template-select" value={templates.some((t) => t.name === tpl) ? tpl : ""} onChange={(e) => pick(e.target.value)} className="mt-1 w-full bg-white border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-accent">
                <option value="">{templates.length ? "— choose an approved template —" : "Loading templates…"}</option>
                {templates.map((t) => <option key={`${t.name}-${t.language}`} value={t.name}>{`${t.name}${t.name === content?.wa_welcome_template_name ? " · Welcome (default)" : ""} · ${t.language} · ${t.category}`}</option>)}
              </select>
            </label>
            {chosen && chosen.var_count > 0 && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Body variables {"{{1}}, {{2}}…"} in order</span>
                <input data-testid="message-template-vars" value={vars} onChange={(e) => setVars(e.target.value)} placeholder="e.g. name,program,url" className="mt-1 w-full bg-white border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-accent" />
                {mismatch && <span className="block text-[11px] text-red-700 mt-1" data-testid="message-template-var-mismatch">Template has {chosen.var_count} variable(s) but {vars.split(",").filter((x) => x.trim()).length} mapped.</span>}
              </label>
            )}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Message preview</p>
              <div data-testid="message-preview-text" className={`mt-1 rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap max-h-56 overflow-auto ${isWa ? "bg-[#DCF8C6] rounded-tl-sm" : "bg-muted/40 border border-border"}`}>
                {!tpl ? <span className="text-muted-foreground">Choose a template to see its message.</span> : loading ? <span className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading message…</span> : preview?.error ? <span className="text-red-700">{preview.error}</span> : preview?.text || "—"}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="message-preview-cancel" onClick={onClose} className="rounded-full border border-border px-5 py-2 text-sm hover:bg-muted transition-colors">Cancel</button>
              <button type="button" data-testid="message-preview-next" onClick={() => setStep(2)} disabled={!tpl || loading || !!preview?.error || !to} className="inline-flex items-center gap-2 rounded-full bg-[#1A2E26] text-white px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50">Next <ChevronRight size={14} /></button>
            </div>
          </div>
        ) : (
          <div className="mt-5">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm" data-testid="message-confirm-text">
              <p className="font-medium">Please confirm</p>
              <p className="mt-1">You are about to send the <code className="bg-white/70 rounded px-1">{tpl}</code> message to <span className="font-medium">{r.name}</span> via <span className="font-medium">{isWa ? `WhatsApp (${to})` : `email (${to})`}</span>.</p>
              {!isWa && preview?.subject && <p className="mt-1 text-xs text-muted-foreground">Subject: {preview.subject}</p>}
            </div>
            <div data-testid="message-preview-text" className={`mt-3 rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap max-h-48 overflow-auto ${isWa ? "bg-[#DCF8C6] rounded-tl-sm" : "bg-muted/40 border border-border"}`}>{preview?.text}</div>
            <div className="mt-6 flex justify-between gap-2">
              <button type="button" data-testid="message-preview-back" onClick={() => setStep(1)} disabled={sending} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm hover:bg-muted transition-colors"><ArrowLeft size={14} /> Back</button>
              <div className="flex gap-2">
                <button type="button" data-testid="message-preview-cancel" onClick={onClose} disabled={sending} className="rounded-full border border-border px-5 py-2 text-sm hover:bg-muted transition-colors">Cancel</button>
                <button type="button" data-testid="message-preview-ok" onClick={send} disabled={sending} className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm text-white transition-colors disabled:opacity-50 ${isWa ? "bg-[#128C7E] hover:bg-[#0f7568]" : "bg-[#1A2E26] hover:opacity-90"}`}>
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {sending ? "Sending…" : "Yes, send now"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
